// /cameras — live camera wall. Home for every role.
//
// Locations are sourced from GET /api/locations (DDB-backed; populated by
// the Ring App Store install bootstrap). Per-location cameras are sourced
// from GET /api/cameras?location_id=<id> in parallel after the location list
// loads. Server enforces grants — the page does no client-side filtering.
//
// The legacy mocked CameraTile shape (CameraMock) survives in @/components
// so the per-tile preview gradients / view labels keep their look. We adapt
// the server `Camera` shape into a CameraMock at render time; preview
// gradients are derived deterministically from `doorbot_id` so each tile
// keeps a stable color across reloads.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/api";
import { Button, LegacyCameraTile, StatusChip } from "@/components";
import { useRole } from "@/role";
import { capabilitiesFor } from "@/role/capabilities";
import {
  cameraLocationsClient,
  type CameraDomainLocation,
} from "@/api/cameraLocationsClient";
import { camerasClient, type Camera } from "@/api/camerasClient";
import { adaptCameraForTile } from "@/utils/cameraAdapter";
import {
  visibleLocations,
  allLocationsHidden,
  type LocationCameraState,
} from "./visibleLocations";
import "./Cameras.css";

// sessionStorage key for the "show empty locations" toggle (survives in-session
// navigation; defaults OFF so empty cards are hidden).
const SHOW_EMPTY_KEY = "reb.cameras.showEmpty";

function readShowEmpty(): boolean {
  try {
    return window.sessionStorage.getItem(SHOW_EMPTY_KEY) === "true";
  } catch {
    return false;
  }
}

type LocationCamerasState =
  | { kind: "loading" }
  | { kind: "ready"; cameras: Camera[] }
  | { kind: "error"; message: string };

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; locations: CameraDomainLocation[] }
  | { kind: "error"; message: string };

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    return `${err.status} ${err.code ?? err.message}`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export function CamerasPage(): JSX.Element {
  const { currentRole } = useRole();

  const caps = useMemo(() => capabilitiesFor(currentRole), [currentRole]);

  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [byLocation, setByLocation] = useState<
    Record<string, LocationCamerasState>
  >({});

  const [showEmpty, setShowEmpty] = useState<boolean>(() => readShowEmpty());

  useEffect(() => {
    try {
      window.sessionStorage.setItem(SHOW_EMPTY_KEY, String(showEmpty));
    } catch {
      // sessionStorage may be unavailable (private mode / SSR); non-fatal.
    }
  }, [showEmpty]);

  useEffect(() => {
    // Wait for RoleProvider to resolve. On hard refresh the page mounts
    // before sessionStorage.currentRole is restored; firing /api/locations
    // without an X-Acting-Role header trips client.ts's missingRoleHandler
    // and forces a /signin redirect, which looks like an unwanted logout.
    if (currentRole === null) return;
    const ctrl = new AbortController();
    void (async () => {
      try {
        const locations = await cameraLocationsClient.list(ctrl.signal);
        setState({ kind: "ready", locations });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setState({
          kind: "error",
          message: describeError(err, "Failed to load locations"),
        });
      }
    })();
    return () => ctrl.abort();
  }, [currentRole]);

  // Once locations are known, fan out per-location camera fetches. Each
  // location maintains its own loading/error/ready state so a 5xx on one
  // location doesn't blank the whole wall.
  useEffect(() => {
    if (state.kind !== "ready") return;
    const ctrl = new AbortController();
    // Seed loading state for each location synchronously so the UI shows a
    // shimmer immediately instead of an empty grid.
    setByLocation((prev) => {
      const next: Record<string, LocationCamerasState> = { ...prev };
      for (const loc of state.locations) {
        next[loc.location_id] = { kind: "loading" };
      }
      return next;
    });
    for (const loc of state.locations) {
      void (async () => {
        try {
          const cameras = await camerasClient.list({
            locationId: loc.location_id,
            signal: ctrl.signal,
          });
          setByLocation((prev) => ({
            ...prev,
            [loc.location_id]: { kind: "ready", cameras },
          }));
        } catch (err) {
          if (ctrl.signal.aborted) return;
          setByLocation((prev) => ({
            ...prev,
            [loc.location_id]: {
              kind: "error",
              message: describeError(err, "Failed to load cameras"),
            },
          }));
        }
      })();
    }
    return () => ctrl.abort();
  }, [state]);

  const locations = state.kind === "ready" ? state.locations : [];

  // Locations to actually render: confirmed-empty ones (ready + zero cameras)
  // are dropped unless the "Show empty locations" toggle is on. A still-loading
  // or errored location is never hidden. The helper is pure + unit-tested in
  // visibleLocations.test.ts.
  const shownLocations = useMemo(
    () =>
      visibleLocations(
        locations,
        byLocation as Record<string, LocationCameraState>,
        showEmpty,
      ),
    [locations, byLocation, showEmpty],
  );
  const everyLocationHidden = useMemo(
    () =>
      allLocationsHidden(
        locations,
        byLocation as Record<string, LocationCameraState>,
        showEmpty,
      ),
    [locations, byLocation, showEmpty],
  );

  // The /api/locations summary returns camera_count = online_camera_count = 0
  // in the DDB-only path (LocationManager.toDomainFromItem) — the device
  // catalog row is not joined. Compute from the per-location camera lists
  // we already fetch (byLocation) so the stat tiles match what the operator
  // sees in the cards below. We treat ready+0 as "still loading per-location"
  // for any location whose camera list hasn't resolved yet.
  const totalCameras = useMemo(() => {
    let total = 0;
    for (const loc of locations) {
      const entry = byLocation[loc.location_id];
      if (entry?.kind === "ready") total += entry.cameras.length;
      else total += loc.camera_count; // best-effort while loading
    }
    return total;
  }, [locations, byLocation]);
  const onlineCameras = useMemo(() => {
    let online = 0;
    for (const loc of locations) {
      const entry = byLocation[loc.location_id];
      if (entry?.kind === "ready") {
        online += entry.cameras.filter((c) => c.status === "online").length;
      } else {
        online += loc.online_camera_count;
      }
    }
    return online;
  }, [locations, byLocation]);

  return (
    <section className="cameras-page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Live cameras</h1>
          <p className="page-subtitle">{caps.scopeDescription}</p>
          {!showEmpty && (
            <p className="cameras-page__hint">
              Locations with no cameras in your current access are hidden.
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label className="cameras-page__show-empty">
            <input
              type="checkbox"
              checked={showEmpty}
              onChange={(e) => setShowEmpty(e.target.checked)}
            />
            Show empty locations
          </label>
          <Link to="/recordings">
            <Button variant="secondary" size="sm">
              Past recordings
            </Button>
          </Link>
          <Link to="/dashboard">
            <Button variant="secondary" size="sm">
              My session
            </Button>
          </Link>
        </div>
      </header>

      {state.kind === "loading" && (
        <div className="cameras-page__banner">Loading locations…</div>
      )}

      {state.kind === "error" && (
        <div className="cameras-page__banner cameras-page__banner--warn">
          <strong>Failed to load locations.</strong> {state.message}
        </div>
      )}

      {state.kind === "ready" && locations.length === 0 && (
        <div className="cameras-page__banner cameras-page__banner--warn">
          <strong>No locations in scope.</strong> Your active role is{" "}
          <code>{currentRole ?? "—"}</code>, but no locations have been
          discovered yet for this workspace. If you just installed the Ring
          App Store integration, locations sync within a minute — refresh.
        </div>
      )}

      {state.kind === "ready" && locations.length > 0 && (
        <>
          <div className="cameras-page__summary">
            <div className="cameras-page__summary-card">
              <div className="cameras-page__summary-value">
                {shownLocations.length}
              </div>
              <div className="cameras-page__summary-label">
                {showEmpty ? "Locations in scope" : "Locations with cameras"}
              </div>
            </div>
            <div className="cameras-page__summary-card">
              <div className="cameras-page__summary-value">{onlineCameras}</div>
              <div className="cameras-page__summary-label">Cameras online</div>
            </div>
            <div className="cameras-page__summary-card">
              <div className="cameras-page__summary-value">
                {totalCameras - onlineCameras}
              </div>
              <div className="cameras-page__summary-label">
                Cameras offline / degraded
              </div>
            </div>
          </div>

          {everyLocationHidden && (
            <div className="cameras-page__banner">
              <strong>No cameras match your access in any location.</strong>{" "}
              Enable <em>Show empty locations</em> to see every location in your
              scope.
            </div>
          )}

          {shownLocations.map((loc) => {
            const camState = byLocation[loc.location_id] ?? {
              kind: "loading",
            };
            return (
              <section
                key={loc.location_id}
                className="cameras-page__location"
              >
                <div className="cameras-page__location-header">
                  <div>
                    <div className="cameras-page__location-title">
                      {loc.display_name}
                    </div>
                    <div className="cameras-page__location-address">
                      {loc.address ?? "Address unknown"} ·{" "}
                      <code className="mono">{loc.location_id}</code>
                    </div>
                  </div>
                  <div className="cameras-page__location-actions">
                    <StatusChip tone="info">
                      {camState.kind === "ready"
                        ? `${camState.cameras.filter((c) => c.status === "online").length} / ${camState.cameras.length} cameras`
                        : `${loc.online_camera_count} / ${loc.camera_count} cameras`}
                    </StatusChip>
                    <Link
                      to={`/cameras/${encodeURIComponent(loc.location_id)}`}
                    >
                      <Button variant="secondary" size="sm">
                        Open site
                      </Button>
                    </Link>
                  </div>
                </div>

                {camState.kind === "loading" && (
                  <div className="cameras-page__banner">Loading cameras…</div>
                )}
                {camState.kind === "error" && (
                  <div className="cameras-page__banner cameras-page__banner--warn">
                    <strong>Failed to load cameras for this site.</strong>{" "}
                    {camState.message}
                  </div>
                )}
                {camState.kind === "ready" && camState.cameras.length === 0 && (
                  <p className="cameras-page__banner">
                    No cameras match your access here.
                  </p>
                )}
                {camState.kind === "ready" && camState.cameras.length > 0 && (
                  <div className="cameras-page__grid">
                    {camState.cameras.map((cam) => (
                      <LegacyCameraTile
                        key={cam.doorbot_id}
                        camera={adaptCameraForTile(cam, loc.display_name)}
                        size="md"
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </>
      )}
    </section>
  );
}
